//  Dependencies
//  ----------------------------------------------------------------------------
import React, { useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useReactToPrint } from 'react-to-print';
import { addPrintableClass, removePrintableClass } from 'utils/methods/textMethod';
//  -- Components ---------------------
import { ResponsiveContainer } from 'recharts';
//  -- Constants ----------------------
import PrintIcon from '@mui/icons-material/Print';
// import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { Rnd } from 'react-rnd';
import { useFullScreenTabDetect } from 'containers/chat/hooks';
import { useOrientation } from 'components/hooks';
import SentryWrap from 'components/Sentry';
import { useStandards } from 'store/story/selectors';
import { useOrgStandardCodes } from 'store/organizations/selectors';
import { formatStandardDisplay } from 'utils/lib/standardHelper';

const ChartWrap = styled.div`
  width: 100%;
  height: 30vh;
  max-height: 300px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 25vh;
    max-height: 250px;
  }

  @media (max-width: 480px) {
    height: 20vh;
    max-height: 200px;
  }

  @media print {
    & svg text {
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    & .recharts-tooltip-cursor,
    & .recharts-tooltip-cursor-fill,
    & .recharts-active-shape,
    & .recharts-reference-line,
    & .recharts-tooltip-wrapper {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    & svg {
      shape-rendering: crispEdges;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .recharts-bar-rectangle {
      fill-opacity: 1 !important;
      stroke: none !important;
    }

    & rect[fill='rgba(0,0,0,0.06)'],
    & rect[fill='rgba(0,0,0,0)'] {
      display: none !important;
      visibility: hidden !important;
    }

    & text {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      opacity: 1 !important;
      visibility: visible !important;
    }

    height: 400px !important;
    max-height: 400px !important;
    page-break-inside: avoid;
  }
`;

const PrintContent = styled.div`
  background-color: #f6f6f6;
  border-radius: 13px;
  border: 1px solid #000;
  padding: 20px;
  max-width: 80vw;

  @media print {
    & svg text {
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    & text {
      opacity: 1 !important;
      visibility: visible !important;
    }
    position: relative !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 20px !important;
    background-color: white !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: visible !important;

    & > div {
      page-break-inside: avoid;
      break-inside: avoid;
      overflow: visible !important;
    }

    & .recharts-tooltip-cursor,
    & .recharts-tooltip-cursor-fill,
    & .recharts-active-shape,
    & .recharts-reference-line,
    & .recharts-tooltip-wrapper,
    & rect[fill='rgba(0,0,0,0.06)'],
    & rect[fill='rgba(0,0,0,0)'] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    & svg {
      shape-rendering: crispEdges;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .recharts-bar-rectangle {
      fill-opacity: 1 !important;
      stroke: none !important;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    progress {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

const PrintStyles = styled.div`
  @media print {
    .no-print {
      display: none !important;
    }
  }
`;

const flagData = [
  {
    title: 'Red = Urgent Support (high 50% failing, downward trend).',
    backgroundcolor: 'linear-gradient(180deg,#ef476f,#d7265e)',
  },
  {
    title: 'Orange = Needs Attention (moderate 25% failing, stagnant growth).',
    backgroundcolor: 'linear-gradient(180deg, #FFB703, #FB8500)',
  },
  {
    title: 'Green = Sufficient Support (low 10% failing, stable growth).',
    backgroundcolor: 'linear-gradient(180deg, #03989e, #03989e)',
  },
];

const StandardExamNameModal = ({ handleToggleStandardExamNameModal, overallReport }) => {
  const [openFlagModal, setOpenFlagModal] = useState(false);
  const printRef = useRef();
  
  const isTablate = useFullScreenTabDetect();
  const isLandscape = useOrientation();

  const rndPosition = useMemo(() => {
    return {
      x: isTablate && !isLandscape ? 100 : 150,
      y: isTablate && !isLandscape ? -1800 : -800,
    };
  }, [isTablate, isLandscape]);

  const rndflagModalDefaultPosition = useMemo(() => {
    return {
      x: -100,
      y: isTablate && !isLandscape ? -1400 : -500,
    };
  }, [isTablate, isLandscape]);
  const standards = useStandards();
  const enabledStateCodes = useOrgStandardCodes();

  const domainData = useMemo(() => {
    const standardReport = overallReport?.standardReport || [];
    if (!Array.isArray(standardReport) || standardReport.length === 0) {
      return [];
    }

    const standardCounts = {};

    standardReport.forEach(item => {
      if (item.standard) {
        const standardAbbrs = item.standard.split(',').map(s => s.trim());
        standardAbbrs.forEach(standardAbbr => {
          if (!standardCounts[standardAbbr]) {
            const formattedName = formatStandardDisplay(standardAbbr, standards, enabledStateCodes) || standardAbbr;
            standardCounts[standardAbbr] = { name: formattedName, originalAbbr: standardAbbr, failedCount: 0, totalCount: 0 };
          }
          standardCounts[standardAbbr].totalCount += 1;
          if (item.correct === false) {
            standardCounts[standardAbbr].failedCount += 1;
          }
        });
      }
    });

    const processedData = Object.values(standardCounts)
      .filter(item => item.failedCount > 0)
      .map(item => ({ name: item.name, value: item.failedCount }))
      .sort((a, b) => b.value - a.value);

    return processedData;
  }, [overallReport?.standardReport, standards, enabledStateCodes]);


  const toggleFlagModal = () => {
    setOpenFlagModal(!openFlagModal);
  };

  const printCall = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Standard Exam Report',
    pageStyle: '@page { size: A4; margin: 15mm; }',
    onBeforeGetContent: async () => {
      return new Promise((resolve) => {
        addPrintableClass();
        setTimeout(() => resolve(), 100);
      });
    },
    onBeforePrint: async () => {
      addPrintableClass();
    },
    onAfterPrint: async () => {
      removePrintableClass();
    },
    onPrintError: async () => {
      removePrintableClass();
    },
  });

  const handlePrint = () => {
    addPrintableClass();
    setTimeout(() => {
      printCall();
    }, 100);
  };

  const checkScore = () => {
    if (overallReport?.score >= 75) {
      return { text: 'sufficient support', color: 'green' };
    } else if (overallReport?.score >= 50 && overallReport?.score < 75) {
      return { text: 'needs attention', color: 'orange' };
    } else {
      return { text: 'urgent support needed', color: '#df0d14' };
    }
  };

  return (
    <SentryWrap>
    <PrintStyles>
      <div>
        <Rnd
          key={`${isTablate}-${isLandscape}`}
          default={rndPosition}
          bounds="window"
          enableResizing={false}
          style={{ zIndex: 1000 }}
        >
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => handleToggleStandardExamNameModal(false)}
              onTouchStart={() => handleToggleStandardExamNameModal(false)}
              className="no-print"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                cursor: 'pointer',
                backgroundColor: 'red',
                borderRadius: '100%',
                width: '25px',
                height: '25px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloseIcon style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <PrintContent ref={printRef} className="to_be_print standardExamKarlaEsparzaModal">
              {/* Part1 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    lineHeight: '1.2',
                  }}
                >
                  <p
                    style={{ fontSize: '14px', fontWeight: '600', color: '#252525', margin: '0px' }}
                  >
                    {overallReport?.name} (Student)
                  </p>
                  {/* <p
                    style={{ fontSize: '12px', fontWeight: '400', color: '#9CA3AF', margin: '0px' }}
                  >
                    Mountain Elementary
                  </p> */}
                </div>
                <div
                  onClick={handlePrint}
                  onTouchStart={handlePrint}
                  className="no-print"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    backgroundColor: '#000',
                    width: '25px',
                    height: '25px',
                    borderRadius: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <PrintIcon sx={{ width: '18px', height: '18px' }} />
                </div>
              </div>
              {/* Part2 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                  marginTop: '5px',
                }}
              >
                <div style={{ width: '50%' }}>
                  {/* left item */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      gap: '5px',
                    }}
                  >
                    <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>
                      Overall Performance
                    </p>
                    <progress
                      className={`pill-progress ${checkScore()?.color}`}
                      id="file"
                      value={overallReport?.score}
                      max="100"
                    >
                      {overallReport?.score}%
                    </progress>
                    <p style={{ fontSize: '12px', fontWeight: '400', margin: '0px' }}>
                      {checkScore()?.text} the exam by {overallReport?.score}%.
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: checkScore()?.color,
                          padding: '2px 10px',
                          borderRadius: '38px',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#fff',
                            lineHeight: '1.2',
                            padding: '2px 4px',
                            margin: '0',
                          }}
                        >
                          {checkScore()?.text}
                        </p>
                        <InfoOutlineIcon
                          onClick={toggleFlagModal}
                          onTouchStart={toggleFlagModal}
                          sx={{ color: '#000', width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                      </div>
                      {/* <div
                      style={{
                        border: '1px solid #000',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff',
                        padding: '2px 10px',
                        borderRadius: '38px',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#000',
                          lineHeight: '1.2',
                          padding: '2px 4px',
                          margin: '0',
                        }}
                      >
                        Compared to District Avg:-22%
                      </p>
                    </div> */}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: '10px',
                    gap: '10px',
                  }}
                >
                  <div style={{ flex: '1 1 300px', minWidth: '240px', maxWidth: '100%' }}>
                    <ChartWrap style={{ borderRadius: '8px' }}>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          margin: '0',
                          textAlign: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        Standards Needing Support
                      </p>
                      {overallReport?.standardReport?.length === 0 ? (
                        <p style={{ fontSize: '12px', fontWeight: '400', margin: '0px', textAlign: 'center' }}>
                            No standards data available
                          </p>
                        ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={domainData}
                            layout="vertical"
                            margin={{ top: 24, right: 8, bottom: 8, left: 4 }}
                            barSize={80}
                            maxBarSize={100}
                            barCategoryGap={8}
                            barGap={2}
                          >
                            {/* vertical grid lines only (like the screenshot) */}
                            <CartesianGrid strokeA="3 3" vertical horizontal={false} />

                            {/* Legend with a circular dot labeled "Domains" */}
                            <Legend
                              verticalAlign="top"
                              align="center"
                              iconType="circle"
                              iconSize={10}
                              wrapperStyle={{ paddingBottom: 8 }}
                            />

                            {/* Number axis across the bottom */}
                            <XAxis
                              type="number"
                              dataKey="value"
                              tick={{ fill: '#61677E', fontSize: 11 }}
                              tickMargin={4}
                              axisLine={false}
                              tickLine={false}
                            />

                            {/* Category labels on the left */}
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={60}
                              tick={{ fill: '#61677E', fontSize: 11 }}
                              axisLine={false}
                              tickLine={false}
                            />

                            {/* Hover tooltip */}
                            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.06)' }} />

                            {/* Bars: orange, rounded on the RIGHT side only; active bar brighter */}
                            <Bar
                              name="Standards"
                              dataKey="value"
                              fill="#ffa51f"
                              barSize={90}
                              radius={[0, 10, 10, 0]} // TL, TR, BR, BL
                              fillOpacity={1}
                              activeBar={{ style: { fillOpacity: 0.8 } }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </ChartWrap>
                  </div>
                  {/* <div style={{ flex: '1 1 250px', minWidth: '200px', maxWidth: '100%' }}>
                  <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <p style={{ fontSize: '12px', fontWeight: '600', margin: '0px' }}>
                      Suggested Next Steps:
                    </p>
                    <div style={{ marginLeft: '20px' }}>
                      <ul>
                        <li style={{ fontSize: '12px', fontWeight: '600' }}>
                          Provide PD on teaching informational text (RI.6.3)
                        </li>
                        <li style={{ fontSize: '12px', fontWeight: '600' }}>
                          Assign targeted small groups for RL.6.2
                        </li>
                        <li style={{ fontSize: '12px', fontWeight: '600' }}>
                          Monitor growth on writing standards (W.6.1)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div> */}
                </div>
              </div>

              {/* Part2 */}
            </PrintContent>
          </div>
        </Rnd>
        {openFlagModal && (
          <Rnd
            default={rndflagModalDefaultPosition}
            bounds="window"
            enableResizing={false}
            style={{ zIndex: 1001 }}
            className="no-print"
          >
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                  cursor: 'pointer',
                  backgroundColor: 'red',
                  borderRadius: '100%',
                  width: '25px',
                  height: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloseIcon
                  onClick={toggleFlagModal}
                  onTouchStart={toggleFlagModal}
                  style={{ width: 20, height: 20, color: 'white' }}
                />
              </div>
              <div className="standardExamScoreFlagIcon">
                <p style={{ fontSize: '20px', fontWeight: '600', color: '#fff', margin: '0px' }}>
                  Flag System
                </p>
                <ul>
                  {flagData.map((item, index) => {
                    return (
                      <li
                        key={index}
                        style={{
                          margin: 0,
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: '20px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 3,
                              background: item.backgroundcolor,
                            }}
                          />
                          <span style={{ flex: 1 }}>{item.title}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </Rnd>
        )}
        </div>
      </PrintStyles>
    </SentryWrap>
  );
};

export default StandardExamNameModal;
